import { useEffect, useRef, useState } from "react";

// One global bubble for every element carrying a `data-tip` attribute (hover, keyboard focus or tap).
export function GlobalTips() {
  const [tip, setTip] = useState(null);
  const timer = useRef(null);
  const hideTimer = useRef(null);
  const lastTouch = useRef(0);

  useEffect(() => {
    const show = (el, delay) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const r = el.getBoundingClientRect();
        const below = r.bottom + 160 < window.innerHeight;
        const half = Math.min(280, window.innerWidth / 2 - 12);
        setTip({
          text: el.getAttribute("data-tip"),
          x: Math.min(Math.max(r.left + r.width / 2, half + 12), window.innerWidth - half - 12),
          y: below ? r.bottom + 8 : r.top - 8,
          below,
        });
      }, delay);
    };
    const hide = () => { clearTimeout(timer.current); clearTimeout(hideTimer.current); setTip(null); };
    const onOver = (e) => {
      // Ignore the mouseover/focus that mobile browsers emulate right after a tap.
      if (Date.now() - lastTouch.current < 1000) return;
      const el = e.target.closest?.("[data-tip]");
      el ? show(el, 250) : hide();
    };
    const onTouch = (e) => {
      lastTouch.current = Date.now();
      const el = e.target.closest?.("[data-tip]");
      if (!el) return hide();
      show(el, 0);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(hide, 2500);
    };
    document.addEventListener("mouseover", onOver);
    document.addEventListener("focusin", onOver);
    document.addEventListener("touchstart", onTouch, { passive: true });
    document.addEventListener("scroll", hide, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("focusin", onOver);
      document.removeEventListener("touchstart", onTouch);
      document.removeEventListener("scroll", hide, true);
    };
  }, []);

  if (!tip) return null;
  return (
    <div
      role="tooltip"
      data-testid="global-tip"
      className="fixed z-[100] max-w-[min(560px,calc(100vw-24px))] bg-[#002FA7] text-white text-[13.5px] leading-relaxed px-4 py-3 shadow-xl pointer-events-none animate-in fade-in-0 zoom-in-95"
      style={{ left: tip.x, top: tip.y, transform: `translate(-50%, ${tip.below ? "0" : "-100%"})`, fontFamily: "IBM Plex Sans, sans-serif" }}
    >
      {tip.text}
    </div>
  );
}

export default GlobalTips;
